import React, { FC } from 'react';
import { SERVICE_TYPE } from '@baemingo/order-status-events/dist/types';
import { StorageData, useStorage } from '~Components/Storage';
import Toggle from '~ComponentsImproved/Toggle';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useButton, useI18n, useLicenses, useLogger, useSelector, useStaffCan } from '~Hooks';
import { PageType, SettingsBlock } from '~Scenes/Settings-v2';
import { BlueButton, Box } from '~Styles';
import DeviceSettings from './DeviceSettings';
import Logout from '~Scenes/Settings-v2/Components/Logout';
import { useHistory } from 'react-router-dom';
interface Props {
  setSelectedPage: (page: PageType | 'developer') => void;
}

const OtherSettings: FC<Props> = ({ setSelectedPage }) => {
  const { i18n } = useI18n();
  const venueId = useSelector((state) => state.venueId);
  const [storage, commitToStorage, storageLoading] = useStorage();
  const flags = useSelector((state) => state.companyFlags);
  const licenses = useLicenses();
  const logger = useLogger('settings-v2');
  const staffCan = useStaffCan();
  const history = useHistory();

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (Other Settings)', { fields: data });

    commitToStorage(data);
  }

  const update = (data: Partial<StorageData>) => {
    logger.info('Updating Settings (Other Settings)', { fields: { data } });

    if ('serviceType' in data) {
      if (data.serviceType) {
        // If it is true that means it is take away is default
        updateStorage({ serviceType: SERVICE_TYPE.TAKE_AWAY });
      } else {
        updateStorage({ serviceType: SERVICE_TYPE.EAT_HERE });
      }
    } else {
      updateStorage(data);
    }
  };

  const togglesConfig: Array<{
    label: string;
    subLabel?: string;
    key: string;
    storage: any;
    // Will reverse the value of the storage
    inverted?: boolean;
  }> = [
    {
      label: i18n('Settings.DefaultTakeAway'),
      key: 'serviceType',
      storage: storage.serviceType === SERVICE_TYPE.TAKE_AWAY,
    },
    {
      label: i18n('Settings.HideServiceType'),
      subLabel: i18n('Settings.HideServiceType.Text'),
      key: 'hideServiceType',
      storage: storage.hideServiceType,
    },
    {
      label: 'Disable Adyen print retry',
      subLabel: 'Disables retry attempts when printing via the Adyen terminal',
      key: 'disableAdyenPrintRetry',
      storage: storage.disableAdyenPrintRetry,
    },
  ];

  if (licenses.hasEPOS) {
    togglesConfig.push({
      label: i18n('Settings.AlwaysPrintReceiptEPOS'),
      key: 'alwaysPrintReceiptEPOS',
      storage: storage.alwaysPrintReceiptEPOS,
    });
  }

  if (licenses.hasKDS) {
    togglesConfig.push({
      label: i18n('Settings.ServiceTypeDelivery.Text'),
      key: 'enableServiceTypeDelivery',
      storage: storage.enableServiceTypeDelivery,
    });
  }

  if (flags.hasPoints) {
    togglesConfig.push({
      label: i18n('Settings.PrintChequeQR'),
      subLabel: i18n('Settings.PrintChequeQR.Text'),
      key: 'printQROnCheque',
      storage: storage.printQROnCheque,
    });
  }

  const devButton = useButton({
    onClick: () => setSelectedPage('developer'),
  });

  const oldHistoryButton = useButton({
    onClick: () => history.push(`/venue/${venueId}/history`),
  });

  if (storageLoading) {
    return null;
  }

  return (
    <Box padding="large">
      <Box>
        <Heading level={4} bottom={8} color="var(--colorWhite)">
          {i18n('Settings.Title')}
        </Heading>
      </Box>
      <SettingsBlock>
        {togglesConfig.map((toggle) => (
          <Toggle
            key={toggle.key}
            settingKey={toggle.key}
            label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
            description={toggle.subLabel}
            initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
            onToggle={() => update({ [toggle.key]: !toggle.storage })}
            fullWidth
            darkBackground
            lightText
          />
        ))}
      </SettingsBlock>

      <Box marginTop="xl" marginBottom="xl">
        <Heading level={4} bottom={8} color="var(--colorWhite)">
          {i18n('Settings.Device')}
        </Heading>
        <DeviceSettings />
      </Box>

      <Box marginBottom="xl">
        <Heading level={4} bottom={8} color="var(--colorWhite)">
          {i18n('Settings.LegacyHistory')}
        </Heading>
        <BlueButton {...oldHistoryButton}>{i18n('Settings.GoToOldHistory')}</BlueButton>
      </Box>

      <Box marginBottom="xl">
        <Heading level={4} bottom={8} color="var(--colorWhite)">
          {i18n('Settings.developer')}
        </Heading>
        <BlueButton {...devButton}>Developer</BlueButton>
      </Box>

      {staffCan.LOG_OUT && (
        <Box marginBottom="xl">
          <Heading level={4} bottom={8} color="var(--colorWhite)">
            {i18n('Settings.LogoutTitle')}
          </Heading>
          <Logout />
        </Box>
      )}
    </Box>
  );
};

export default OtherSettings;
